import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthClientConfig, AuthService, User } from '@auth0/auth0-angular';
import * as Sentry from '@sentry/angular';
import { Observable, of, Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { isAnonymousAccessibleSharedUrl } from '../utils';
import {
  Auth0Error,
  AuthableRole,
  BranchRole,
  KnownAuthErrorTypes,
  LocationRole,
  LoginQueryParams,
  UserPreferences,
  UserProfile,
} from '../types';
import { LoggedInCallback } from './cognito.service';
import { CustomerIOService } from './customerio.service';
import { DebugService } from './debug.service';

export const isOfTypeAuth0Error = (err: any): err is Auth0Error =>
  err !== null && typeof err === 'object' && 'error' in err && 'error_description' in err;

@Injectable()
export class UserLoginService {
  public debugService = inject(DebugService);
  public auth = inject(AuthService);
  private customerIO = inject(CustomerIOService);

  public currentUser = signal<User | undefined>(undefined);
  public currentProfile = signal<UserProfile | undefined>(undefined);
  public currentProfileLoaded = new Subject<UserProfile>();
  public currentProfileLoaded$: Observable<UserProfile> = this.currentProfileLoaded.pipe(shareReplay<UserProfile>());
  public router = inject(Router);

  public currentUserAcceptedTOS = signal<boolean>(true);

  public currentProfileRoleList = computed(() => {
    if (this.debugService.hasRoleOverrides()) {
      return this.debugService.roleOverrideList();
    }

    return [
      ...(this.currentProfile()?.roles?.system ?? []),
      ...(this.currentProfile()?.branch_roles ?? []),
      ...(this.currentProfile()?.location_roles ?? []),
    ];
  });

  public currentUserIsAdmin = computed(() => this.currentProfileRoleList().includes('admin'));
  public currentUserIsAdminOrInternalUser = computed(() =>
    this.currentProfileRoleList().some((role) => ['admin', 'investigator', 'validator'].includes(role)),
  );
  public currentUserIsGuest = computed(() =>
    Object.keys(this.currentProfile()?.roles?.location ?? {}).some((k) => this.currentProfile()?.roles?.location[k].includes('guest')),
  );

  private http = inject(HttpClient);
  private apiHost = `${environment.apiBaseUrl}`;
  private authClientConfig = inject(AuthClientConfig);

  getAndSetMe() {
    this.getMe().subscribe((prof) => {
      this.currentProfile.set(prof);
      this.currentProfileLoaded.next(prof);

      this.customerIO.identifyUser(prof.email, {
        email: prof.email,
        firstName: prof.preferences.first_name,
        lastName: prof.preferences.last_name,
        username: prof.email,
        name: prof.name,
        id: prof.user_id,
      });

      const tosAccepted = !!prof.preferences?.tos_accepted_at;
      this.currentUserAcceptedTOS.set(tosAccepted);
    });
  }

  getMe(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.apiHost}/UserManagement/Me`, { priority: 'high' });
  }

  updateMyProfile(prefs: UserPreferences) {
    return this.http.put(`${this.apiHost}/UserManagement/Me/Profile`, prefs);
  }

  resetPassword(email: string) {
    const url = `https://${environment.auth0Domain}/dbconnections/change_password`;
    const payload = {
      client_id: environment.auth0ClientId,
      email: email,
      connection: 'Username-Password-Authentication',
    };
    return this.http.post(url, payload, { responseType: 'text' });
  }

  getUserConnection(email: string): Observable<string> {
    return this.http.get(`${this.apiHost}/UserManagement/GetUserConnection`, {
      params: { email },
      responseType: 'text' as const,
    });
  }

  passwordLessStart(email: string) {
    const url = `https://${environment.auth0Domain}/passwordless/start`;
    const payload = {
      client_id: environment.auth0ClientId,
      email: email,
      connection: 'email',
      send: 'code',
    };
    return this.http.post(url, payload, { responseType: 'text' });
  }

  logout(returnTo?: string) {
    this.auth.logout({
      logoutParams: {
        returnTo: returnTo ?? `${document.location.origin}/logged-out`,
      },
    });

    this.customerIO.resetUser();
  }

  logoutAndRedirectToLogin() {
    this.auth.logout({
      logoutParams: {
        returnTo: `${document.location.origin}/login`,
      },
    });

    this.customerIO.resetUser();
  }

  navigateToLogin(returnUrl?: string) {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const path = url.pathname;

    let target = returnUrl;

    // If no explicit returnUrl was provided, use the current URL as the return target after login.
    if (!target) {
      if (path === '/login') {
        // Already on login page; preserve the existing returnUrl if present.
        target = url.searchParams.get(LoginQueryParams.RETURN_URL) ?? '/';
      } else {
        target = this.router.url;
      }
    }

    // Avoid repeatedly wrapping the same login URL with itself. If we're already on the login page and the existing returnUrl matches the target, do nothing. (returnURL was appending on every refresh on login page)
    const existingReturnUrl = url.searchParams.get(LoginQueryParams.RETURN_URL);
    if (path === '/login' && existingReturnUrl === target) {
      return;
    }

    this.router.navigate(['/login'], {
      queryParams: {
        [LoginQueryParams.RETURN_URL]: target,
      },
    });
  }

  isAuthenticated(callback: LoggedInCallback) {
    if (callback == null) {
      throw 'UserLoginService: Callback in isAuthenticated() cannot be null';
    }

    /**
     * WARNING!!!
     *     This subscription can be triggered from app.component.ts.
     *     app.component doesn't know which Auth0 client to use.
     *
     *     We must keep this in mind in case we use `loginWithRedirect`,
     *     pass appropriate Auth0 client info.
     */
    this.auth.error$.subscribe((err) => {
      console.error(err);
      console.log(JSON.stringify(err));
      if (isOfTypeAuth0Error(err)) {
        const type = err?.error as KnownAuthErrorTypes;
        // An anonymous viewer of a page that allows anonymous viewing has no session to fix, and that page handles its own 401s.
        // Anyone with a saved login, even an expired one, still goes back through login.
        const isAnonymousViewer = !this.currentUser() && isAnonymousAccessibleSharedUrl(window.location.pathname);

        if (
          [KnownAuthErrorTypes.MissingRefreshToken, KnownAuthErrorTypes.InvalidGrant, KnownAuthErrorTypes.InvalidRequest].includes(type) &&
          !isAnonymousViewer
        ) {
          // These errors can occur when the user's session has expired or is otherwise invalid
          // In these cases, we want to redirect the user to the login page to re-authenticate
          // Store the current URL so we can return the user to where they were after they log in again
          sessionStorage.setItem('post_login_redirect', window.location.href);
          this.logoutAndRedirectToLogin();
        }
      }
    });

    this.auth.isAuthenticated$.subscribe((status) => {
      callback.isLoggedIn(null, status);
    });

    this.auth.user$.subscribe((user) => {
      this.currentUser.set(user);

      if (user) {
        Sentry.setUser({ email: user.email, username: user.name, id: user.sub });
      } else {
        Sentry.setUser({ username: 'anonymous' });
      }
    });
  }

  canGrantBranchPermission(permission: BranchRole, branch: string): boolean {
    const isAdmin = this.currentUserIsAdmin();
    const isBranchOwner = this.currentProfile()?.roles.branch?.[branch]?.includes('branch-owner');
    const isBranchManager = this.currentProfile()?.roles.branch?.[branch]?.includes('branch-manager');

    const matrix: Record<BranchRole, boolean> = {
      'branch-owner': isAdmin,
      'branch-manager': isAdmin || isBranchOwner,
      'market-manager': isAdmin || isBranchOwner || isBranchManager,
      installer: isAdmin || isBranchOwner || isBranchManager,
      'self-service': isAdmin,
      'self-service-investigator': isAdmin,
    };

    return matrix[permission];
  }

  canGrantLocationPermission(permission: LocationRole, branch: string, location?: string): boolean {
    const isAdmin = this.currentUserIsAdmin();
    const isBranchOwner = this.currentProfile()?.roles.branch?.[branch]?.includes('branch-owner');
    const isBranchManager = this.currentProfile()?.roles.branch?.[branch]?.includes('branch-manager');
    const isMarketManager = this.currentProfile()?.roles.branch?.[branch]?.includes('market-manager');
    const isLocationManager = this.currentProfile()?.roles.location?.[location]?.includes('location-manager');

    const matrix: Record<LocationRole, boolean> = {
      guest: isAdmin || isBranchManager || isMarketManager || isBranchOwner || isLocationManager,
      'location-manager': isAdmin || isBranchOwner,
      'market-health-manager': isAdmin,
    };
    return matrix[permission];
  }

  canCreateBranchUsers(branchId: string): boolean {
    return this.currentUserIsAdmin() || this.hasSomeBranchRoles(['branch-owner', 'branch-manager'], branchId);
  }

  canGetBranchUsers(branchId: string): boolean {
    return this.currentUserIsAdmin() || this.hasSomeBranchRoles(['branch-owner', 'branch-manager'], branchId);
  }

  canAccessSuspendedBranchBilling(branchId: number): boolean {
    return this.currentUserIsAdmin() || this.hasSomeBranchRoles(['branch-owner', 'branch-manager'], branchId.toString());
  }

  canManageAssetRecovery(branchId: string): boolean {
    return this.currentUserIsAdmin() || this.hasSomeBranchRoles(['branch-owner', 'branch-manager', 'market-manager'], branchId);
  }

  private hasBranchRole(role: BranchRole, branchId: string): boolean {
    return this.currentProfile()?.roles.branch?.[branchId]?.includes(role) ?? false;
  }

  private hasSomeBranchRoles(roles: BranchRole[], branchId: string): boolean {
    return roles.some((role) => this.hasBranchRole(role, branchId));
  }

  currentUserHasRole(role: AuthableRole): boolean {
    return this.currentProfileRoleList().includes(role);
  }

  checkIfAcceptedTOS(): Observable<boolean> {
    const tosAcceptedAt = this.currentProfile()?.preferences?.tos_accepted_at;
    return of(!!tosAcceptedAt);
  }

  acceptTOS() {
    this.http.post<{ tosAcceptedAt: string }>(`${this.apiHost}/UserManagement/AcceptTOS`, {}).subscribe(() => {
      this.currentUserAcceptedTOS.set(true);

      this.getAndSetMe();
    });
  }
}
